import { differenceInMinutes, isAfter, isBefore } from 'date-fns';

import { type merged_time_and_activity } from '@common/@types/prisma';
import { type StudentLearningTime } from '@common/@types/learning_time';
import { type LearningSessionDisplay } from '@common/components/daily-activity/types';
import { getLearningSessionDisplay } from '@common/components/daily-activity/formatters/learningSession/RadialGaugeLearningSessionFormatter';

import { isCurrentTimeInRange, convertTimeStringToDate } from './dateTimeUtils';
import { isNonLearningSlot } from './dataUtils';
import {
  type StudentScheduleSlot,
  type SubjectMetrics,
  type CompleteLearningSessionDisplay,
  type SubjectAverageMetrics,
} from '../../model';
import { getPercetageValue } from '../../utils';

const EACH_SUBJECT_TIME_IN_MINUTES = 25;
const TOTAL_CLOCK_OUTER_DOTS = 180;

export const TOTAL_CIRCLE_ANGLE = 360;
export const ONE_DOT_ANGLE = TOTAL_CIRCLE_ANGLE / TOTAL_CLOCK_OUTER_DOTS;

/**
 * Generates an array of outer dots angles for an analog clock.
 */
export const generateClockOuterDotsAngles = () => {
  const angles: number[] = [];

  for (let i = 0; i < TOTAL_CLOCK_OUTER_DOTS; i++) {
    const angle = i * ONE_DOT_ANGLE;
    angles.push(angle);
  }

  return angles;
};

export const convertToNearestMultiple = (value: number, multiple: number): number =>
  Math.round(value / multiple) * multiple;

const transformMetrics = (data: LearningSessionDisplay): CompleteLearningSessionDisplay => {
  const { actual, target } = data;
  const completedPercentage = getPercetageValue(target, actual);
  const mastered = actual >= target;

  return {
    ...data,
    completedPercentage,
    mastered,
  };
};

export const getSubjectStatsMap = (
  data: Array<merged_time_and_activity> | undefined,
  theme: any
) => {
  const results: Record<string, SubjectMetrics> = {};

  if (!data) {
    return results;
  }

  data.forEach((item) => {
    const [timeSpent, accuracy, correctQuestionsPersHour, mastery] = getLearningSessionDisplay(
      item,
      theme
    );

    const key = item.subject?.toLowerCase();

    results[key] = {
      timeSpent: transformMetrics(timeSpent),
      accuracy: transformMetrics(accuracy),
      mastery: transformMetrics(mastery),
      correctQuestionsPersHour: transformMetrics(correctQuestionsPersHour),
    };
  });

  return results;
};

export const getSubjectTimeSpentMap = (
  data: Record<string, StudentLearningTime[]> | undefined,
  firstHalfSchedule: Record<string, StudentScheduleSlot>
): Record<string, [number, number]> => {
  const results: Record<string, [number, number]> = {};
  const midDay = convertTimeStringToDate('12:00 PM');

  if (!data) {
    return results;
  }

  for (const [key, learningTimeList] of Object.entries(data)) {
    const primaryKey = key.toLowerCase();

    const currentSubject = firstHalfSchedule[primaryKey];

    if (!currentSubject) {
      continue;
    }

    let subjectTimeSpent = 0;
    let repeatedSubjectTimeSpent = 0;

    for (const learningTime of learningTimeList) {
      if (isAfter(new Date(learningTime.start_time), midDay)) {
        repeatedSubjectTimeSpent += learningTime?.duration_minutes ?? 0;
      } else {
        subjectTimeSpent += learningTime?.duration_minutes ?? 0;
      }
    }

    results[primaryKey] = [Math.round(subjectTimeSpent), Math.round(repeatedSubjectTimeSpent)];
  }

  return results;
};

export const getSelectedSubjectStats = (
  statsMap: Record<string, SubjectMetrics>,
  selectedSubject: StudentScheduleSlot | null
): SubjectMetrics => {
  const key = selectedSubject?.slotLabel?.toLowerCase();

  if (!key) {
    return {} as SubjectMetrics;
  }

  return statsMap[key] ?? ({} as SubjectMetrics);
};

export const getTimeSaved = (
  firstHalfSchedule: Record<string, StudentScheduleSlot>,
  subjectStatsMap: Record<string, SubjectMetrics>,
  timeSpentMap: Record<string, [number, number]>
): number => {
  let totalTimeSaved = 0;

  // AI-GEN START - Cursor - GPT4
  for (const subject of Object.values(firstHalfSchedule)) {
    const currentSubjectStats = getSelectedSubjectStats(subjectStatsMap, subject);
    if (currentSubjectStats?.accuracy?.mastered && currentSubjectStats?.mastery?.mastered) {
      totalTimeSaved = totalTimeSaved + EACH_SUBJECT_TIME_IN_MINUTES;
    }
  }
  // AI-GEN END - Cursor - GPT4

  return totalTimeSaved;
};

export const getSelectedSubjectTimeSpent = (
  timeSpentMap: Record<string, [number, number]>,
  selectedSubject: StudentScheduleSlot | null
): [number, number] => {
  const key = selectedSubject?.slotLabel?.toLowerCase();

  if (!key || !timeSpentMap) {
    return [0, 0];
  }

  return timeSpentMap[key] ?? [0, 0];
};

export const getActiveScheduleSlot = (
  data: Array<StudentScheduleSlot>,
  currentTime: Date
): StudentScheduleSlot | null => {
  let result: StudentScheduleSlot | null = null;

  for (let i = 0; i < data.length; i++) {
    const { startTime, endTime } = data[i];

    if (isCurrentTimeInRange(currentTime, startTime, endTime)) {
      result = data[i];
      break;
    }
  }

  return result;
};

export const getNumberOfSlotCompletedOrActive = (
  data: Array<StudentScheduleSlot>,
  currentTime: Date
): number => {
  let completed = 0;

  for (let i = 0; i < data.length; i++) {
    const { startTime } = data[i];

    if (isBefore(startTime, currentTime)) {
      completed += 1;
    }
  }

  return completed;
};

export const getNextStudySlot = (
  data: Array<StudentScheduleSlot>,
  currentTime: Date
): StudentScheduleSlot | null => {
  const numberOfSlotsCompleted = getNumberOfSlotCompletedOrActive(data, currentTime);
  let nextClass: StudentScheduleSlot | null = null;

  for (let i = numberOfSlotsCompleted; i < data.length; i++) {
    const currentSession = data[i];

    if (!isNonLearningSlot(currentSession.slotLabel)) {
      nextClass = currentSession;
      break;
    }
  }

  return nextClass;
};

export const compareArrays = (a: (string | number)[], b: (string | number)[]): boolean => {
  if (a.length !== b.length) return false;
  else {
    // Comparing each element of your array
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) {
        return false;
      }
    }
    return true;
  }
};

export const getDayAverageStats = (data: Record<string, SubjectMetrics>): SubjectAverageMetrics => {
  let accuracyActual = 0;
  let masteryActual = 0;
  let masteryTarget = 0;
  const numOfItems = Object.values(data)?.length;

  for (const [, value] of Object.entries(data)) {
    // AI-GEN - Cursor - GPT4
    const { accuracy, mastery } = value;
    accuracyActual += accuracy.actual;
    masteryActual += mastery.actual;
    masteryTarget += mastery.target;
  }

  const accuracyCompletedPercentage = Math.floor(accuracyActual / numOfItems);

  return {
    accuracy: {
      actual: accuracyCompletedPercentage,
      target: 100,
      completedPercentage: accuracyCompletedPercentage,
    },
    mastery: {
      actual: masteryActual,
      target: masteryTarget,
      completedPercentage: getPercetageValue(masteryTarget, masteryActual),
    },
  };
};

export const isAverageStatsVisible = (
  currentTime: Date,
  isMastered: boolean,
  selectedSubject: StudentScheduleSlot | null
) => {
  const isTimePastThree = differenceInMinutes(convertTimeStringToDate('3:00 PM'), currentTime);
  return (!selectedSubject && isTimePastThree) || (isMastered && selectedSubject?.repeatedClass);
};
